const VEGAN_DIET_PREFERENCE = 'vegan';
const VEGAN_UNAVAILABLE_MEAL_SIZES = ['large', 'lean'];
const VEGAN_FALLBACK_MEAL_SIZE = 'standard';
const SET_MEAL_BLOB_URL = '/apps/simmer/set-meal-blob';
// The bundle manager only knows variants for the next 5 weeks, so it remaps that window and
// leaves later weeks to be built from the portion size — same rules here.
const VARIANT_REMAP_WEEKS = 5;
const WEEK_BOUNDARY_OFFSET_MS = 37 * 60 * 60 * 1000;

const VARIANT_SIBLINGS_QUERY = `
  query VariantSiblings($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on ProductVariant {
        id
        title
        product { variants(first: 250) { nodes { id title } } }
      }
    }
  }
`;

const normalizeMealSize = (value) => {
  const match = String(value || '').trim().toLowerCase().match(/\b(standard|large|lean)\b/);
  return match?.[1] ?? '';
};

const capitalize = (value) => value.charAt(0).toUpperCase() + value.slice(1);

const variantId = (gid) => String(gid || '').split('/').pop();

const selectionsOf = (weekEntry) => (Array.isArray(weekEntry) ? weekEntry : weekEntry?.selections ?? []);

// `Large / 12` -> `Standard / 12`, `Lean` -> `Standard`
const withPortion = (variantTitle, portionLabel) =>
  String(variantTitle || '').replace(/\b(standard|large|lean)\b/i, portionLabel);

// Simmer's week rolls over 37 hours after Monday 00:00 UTC - Tuesday 01:00 PM, matching the bundle manager.
const currentWeekKey = () => {
  const date = new Date(Date.now() - WEEK_BOUNDARY_OFFSET_MS);
  // An ISO week is numbered by the Thursday it contains, so count from there.
  const thursday = new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate() + 4 - (date.getUTCDay() || 7)
  ));
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1);
  const week = Math.ceil(((thursday.getTime() - yearStart) / 86400000 + 1) / 7);

  return `${thursday.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
};

const waitForCF = (timeout = 8000) =>
  new Promise((resolve, reject) => {
    if (window.CF?.customer) return resolve(window.CF);
    const interval = setInterval(() => {
      if (window.CF?.customer) { clearInterval(interval); resolve(window.CF); }
    }, 100);
    setTimeout(() => { clearInterval(interval); reject(new Error('CF not available')); }, timeout);
  });

class CustomerAllergens extends HTMLElement {
  connectedCallback() {
    if (!this.hasAttribute('data-authenticated')) return;

    this.allergyButtons = [...this.querySelectorAll('[js-allergens="allergy-option"]')];
    this.proteinButtons = [...this.querySelectorAll('[js-allergens="protein-option"]')];
    this.saveButton = this.querySelector('[js-allergens="save"]');
    this.saveLabel = this.querySelector('[js-allergens="save-label"]');
    this.saveSpinner = this.querySelector('[js-allergens="save-spinner"]');
    this.feedback = this.querySelector('[js-allergens="feedback"]');
    this.portionModal = this.querySelector('[js-allergens="portion-modal"]');
    this.portionModalConfirm = this.querySelector('[js-allergens="portion-modal-confirm"]');
    this.mealSizeRaw = String(this.dataset.mealSize || '').trim();
    this.mealSize = normalizeMealSize(this.mealSizeRaw);
    this.i18n = {
      saveSuccess: this.dataset.i18nSaveSuccess,
      saveError: this.dataset.i18nSaveError
    };

    this.allergyButtons.forEach(btn => {
      btn.setAttribute('aria-pressed', 'false');
      btn.addEventListener('click', this.handleAllergyClick.bind(this));
    });
    this.proteinButtons.forEach(btn => {
      btn.setAttribute('aria-pressed', 'false');
      btn.addEventListener('click', this.handleProteinClick.bind(this));
    });
    this.saveButton?.addEventListener('click', this.handleSaveClick.bind(this));
    this.portionModalConfirm?.addEventListener('click', () => this.handleSave());

    this.loadSavedState();
  }

  setPageLoading(loading) {
    this.toggleAttribute('data-loading', loading);
    if (this.saveButton) this.saveButton.disabled = loading;
  }

  setSaveLoading(loading) {
    if (this.portionModalConfirm) this.portionModalConfirm.disabled = loading;
    if (!this.saveButton) return;
    this.saveButton.disabled = loading;
    this.saveLabel?.toggleAttribute('hidden', loading);
    this.saveSpinner?.toggleAttribute('hidden', !loading);
  }

  closePortionModal() {
    if (this.portionModal?.matches(':popover-open')) {
      this.portionModal.hidePopover();
    }
  }

  handleAllergyClick(event) {
    const btn = event.currentTarget;
    const isActive = btn.classList.toggle('customer-allergens__allergy-option--is-active');
    btn.setAttribute('aria-pressed', String(isActive));
  }

  handleProteinClick(event) {
    const btn = event.currentTarget;
    this.proteinButtons.forEach(b => {
      b.classList.remove('customer-allergens__protein-option--is-active');
      b.setAttribute('aria-pressed', 'false');
    });
    btn.classList.add('customer-allergens__protein-option--is-active');
    btn.setAttribute('aria-pressed', 'true');
  }

  buildValues() {
    const activeProtein = this.proteinButtons.find(b =>
      b.classList.contains('customer-allergens__protein-option--is-active')
    );
    const allergens = this.allergyButtons
      .filter(b => b.classList.contains('customer-allergens__allergy-option--is-active') && b.dataset.value)
      .map(b => b.dataset.value)
      .join(',');

    return {
      diet_preference: activeProtein?.dataset.value ?? '',
      customer_allergens: allergens
    };
  }

  /**
   * Large and lean portions are not produced for the vegan plan, so switching to vegan has to
   * take the customer's portion size back to standard with it.
   */
  requiresPortionDowngrade() {
    const { diet_preference } = this.buildValues();

    return diet_preference === VEGAN_DIET_PREFERENCE
      && VEGAN_UNAVAILABLE_MEAL_SIZES.includes(this.mealSize);
  }

  getMealSizeMetafieldValue(mealSize) {
    return /^[A-Z]/.test(this.mealSizeRaw) ? capitalize(mealSize) : mealSize;
  }

  parseMealChoices() {
    try {
      return JSON.parse(this.querySelector('[js-allergens="meal-choices"]')?.textContent);
    } catch (err) {
      console.warn('[customer-allergens] Could not parse meal choices:', err);
      return null;
    }
  }

  /** Maps each variant onto the same variant in the target portion, where one exists. */
  async fetchPortionVariantIds(variantIds, portionLabel) {
    const ids = [...new Set(variantIds)].map((id) => `gid://shopify/ProductVariant/${id}`);
    const portionVariantIds = new Map();

    if (!ids.length) return portionVariantIds;

    const apiVersion = this.dataset.storefrontApiVersion || '2025-01';
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.dataset.storefrontAccessToken
      },
      body: JSON.stringify({ query: VARIANT_SIBLINGS_QUERY, variables: { ids } })
    });

    if (!response.ok) throw new Error(`Storefront API request failed (${response.status})`);

    const { data } = await response.json();

    (data?.nodes ?? []).filter(Boolean).forEach((variant) => {
      const portionTitle = withPortion(variant.title, portionLabel).toLowerCase();
      const sibling = variant.product.variants.nodes.find((node) => node.title.toLowerCase() === portionTitle);

      if (sibling) portionVariantIds.set(variantId(variant.id), variantId(sibling.id));
    });

    return portionVariantIds;
  }

  /**
   * Applies a portion change to the customer's meal blob: the portion size is set on every
   * upcoming delivery, and variants are remapped for the next 5 that aren't skipped — a skipped
   * week keeps its meals so they come back unchanged if it is un-skipped. The locked first week
   * is left alone, as it is on the edit plan page.
   */
  async saveMealBlobPortion(portionLabel) {
    const mealChoices = this.parseMealChoices();
    const plan = mealChoices?.plan;

    if (!plan) return;

    const fromWeek = currentWeekKey();
    const isSkipped = (week) => Boolean(plan[week]?.skipped ?? plan[week]?.skip);
    const weeks = Object.keys(plan)
      .filter((week) => /^\d{4}-W\d{2}$/.test(week) && week >= fromWeek && !plan[week]?.firstWeek)
      .sort();
    const remapWeeks = weeks.filter((week) => !isSkipped(week)).slice(0, VARIANT_REMAP_WEEKS);
    const portionVariantIds = await this.fetchPortionVariantIds(
      remapWeeks.flatMap((week) => selectionsOf(plan[week]).map((selection) => selection.variantId)),
      portionLabel
    );

    weeks.forEach((week) => {
      const weekEntry = plan[week];
      const remap = (selections) => (remapWeeks.includes(week)
        ? selections.map((selection) => ({
          ...selection,
          variantId: portionVariantIds.get(String(selection.variantId)) ?? selection.variantId
        }))
        : selections);

      // Legacy weeks are a bare array of selections and have no portion size to set.
      if (Array.isArray(weekEntry)) {
        plan[week] = remap(weekEntry);
        return;
      }

      plan[week] = { ...weekEntry, portionSize: portionLabel };

      if (Array.isArray(weekEntry.selections)) {
        plan[week].selections = remap(weekEntry.selections);
      }
    });

    const response = await fetch(SET_MEAL_BLOB_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mealChoices })
    });

    if (!response.ok) throw new Error(`Failed to save meal choices (${response.status})`);
  }

  handleSaveClick() {
    if (this.requiresPortionDowngrade()) {
      this.portionModal?.showPopover();
      return;
    }

    this.handleSave();
  }

  async handleSave() {
    const downgradePortion = this.requiresPortionDowngrade();

    this.setSaveLoading(true);

    try {
      const cf = await waitForCF();
      const { diet_preference, customer_allergens } = this.buildValues();

      if (downgradePortion) {
        await this.saveMealBlobPortion(capitalize(VEGAN_FALLBACK_MEAL_SIZE));
      }

      cf.customer.set('diet_preference', diet_preference);
      cf.customer.set('customer_allergens', customer_allergens);

      if (downgradePortion) {
        cf.customer.set('meal_size', this.getMealSizeMetafieldValue(VEGAN_FALLBACK_MEAL_SIZE));
      }

      const result = await cf.customer.save();

      if (result?.errors && Object.keys(result.errors).length > 0) {
        console.error('[customer-allergens] Save errors:', result.errors);
        this.showFeedback(this.i18n.saveError, 'error');
      } else {
        if (downgradePortion) {
          this.mealSize = VEGAN_FALLBACK_MEAL_SIZE;
          this.mealSizeRaw = this.getMealSizeMetafieldValue(VEGAN_FALLBACK_MEAL_SIZE);
        }

        this.showFeedback(this.i18n.saveSuccess, 'success');
      }
    } catch (err) {
      console.error('[customer-allergens] Save failed:', err);
      this.showFeedback('There was an error saving your preferences. Please try again.', 'error');
    } finally {
      this.closePortionModal();
      this.setSaveLoading(false);
    }
  }

  async loadSavedState() {
    this.setPageLoading(true);

    try {
      const cf = await waitForCF();
      const [dietPreference, rawAllergens, mealSize] = await Promise.all([
        cf.customer.get('diet_preference'),
        cf.customer.get('customer_allergens'),
        cf.customer.get('meal_size')
      ]);

      if (typeof mealSize === 'string' && normalizeMealSize(mealSize)) {
        this.mealSizeRaw = mealSize.trim();
        this.mealSize = normalizeMealSize(mealSize);
      }

      this.proteinButtons.forEach(btn => {
        const isActive = typeof dietPreference === 'string' && btn.dataset.value === dietPreference;
        btn.classList.toggle('customer-allergens__protein-option--is-active', isActive);
        btn.setAttribute('aria-pressed', String(isActive));
      });

      const savedAllergens = (typeof rawAllergens === 'string' ? rawAllergens : '')
        .split(',').map(v => v.trim()).filter(Boolean);

      this.allergyButtons.forEach(btn => {
        const isActive = savedAllergens.includes(btn.dataset.value);
        btn.classList.toggle('customer-allergens__allergy-option--is-active', isActive);
        btn.setAttribute('aria-pressed', String(isActive));
      });
    } catch (err) {
      console.warn('[customer-allergens] Could not load saved state:', err);
    } finally {
      this.setPageLoading(false);
    }
  }

  showFeedback(message, type) {
    if (!this.feedback) return;
    clearTimeout(this._feedbackTimeout);
    this.feedback.textContent = message;
    this.feedback.dataset.type = type;
    this.feedback.hidden = false;
    this._feedbackTimeout = setTimeout(() => { this.feedback.hidden = true; }, 4000);
  }
}

customElements.define('customer-allergens', CustomerAllergens);
